import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Flex, Heading, FormControl, FormLabel, Input, Select,
  Textarea, Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, NumberInput, NumberInputField,
  useToast, Divider, Text, VStack, Container, InputGroup, InputRightElement, InputLeftElement, Spinner,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
  Badge, Radio, RadioGroup, Stack, Alert, AlertIcon, Skeleton, Grid, GridItem,
  useBreakpointValue
} from "@chakra-ui/react";
import { DeleteIcon, DownloadIcon, SearchIcon, CheckIcon, ArrowBackIcon, AttachmentIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { api, searchProdutosAutoComplete } from "../services/api";
import ProdutoImagem from "../components/ProdutoImagem";
import Preloader from "../components/Preloader";

const EditarListaPromocao = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    origem: "0",
    status: "ativa",
    data_inicio: "",
    data_fim: "",
    tipo_precificacao: "automatica",
  });

  const [existingItems, setExistingItems] = useState([]);
  const [existingPagination, setExistingPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingBusca, setExistingBusca] = useState("");
  const [existingPerPage, setExistingPerPage] = useState(50);
  const [newManualItems, setNewManualItems] = useState([]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [descontoInput, setDescontoInput] = useState(""); 
  const [precoFinalInput, setPrecoFinalInput] = useState(""); 

  const [arquivo, setArquivo] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [itemToDelete, setItemToDelete] = useState(null);
  const cancelRef = React.useRef();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const fetchLista = async () => {
    try {
        const response = await api.get(`/lista-descontos/${id}`);
        const data = response.data;

        setFormData({
            titulo: data.titulo,
            descricao: data.descricao || "",
            origem: data.origem,
            status: data.status,
            data_inicio: data.data_inicio ? data.data_inicio.split('T')[0] : "",
            data_fim: data.data_fim ? data.data_fim.split('T')[0] : "",
            tipo_precificacao: data.tipo_precificacao || "automatica"
        });
    } catch (error) {
        toast({ title: "Erro ao carregar lista", status: "error" });
        navigate("/promocoes");
    } finally {
        setLoadingData(false);
    }
  };

  const fetchExistingItems = useCallback(async (page = 1) => {
    setExistingLoading(true);
    try {
      const params = { page, per_page: existingPerPage };
      if (existingBusca) params.busca = existingBusca;
      const response = await api.get(`/lista-descontos/${id}/itens`, { params });
      setExistingItems(response.data.data);
      setExistingPagination({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        total: response.data.total,
      });
    } catch (error) {
      toast({ title: "Erro ao carregar itens", status: "error" });
    } finally {
      setExistingLoading(false);
    }
  }, [id, existingBusca, existingPerPage, toast]);

  useEffect(() => {
      fetchLista();
  }, [id]);

  useEffect(() => {
    if (!loadingData) fetchExistingItems(1);
  }, [loadingData, fetchExistingItems]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (!loadingData) fetchExistingItems(1);
    }, 400);
    return () => clearTimeout(delay);
  }, [existingBusca]);

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchTerm.length > 0 && !selectedProduct) {
        setIsSearching(true);
        try {
            const result = await searchProdutosAutoComplete(searchTerm);
            if(result) setFilteredProducts(result);
        } catch(e) { console.error(e); } 
        finally { setIsSearching(false); }
      } else {
        setFilteredProducts([]);
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, selectedProduct]);

  useEffect(() => {
    setNewManualItems(prevItems => prevItems.map(item => {
        const isAuto = formData.tipo_precificacao === 'automatica';
        
        const novoPrecoOriginal = isAuto 
            ? parseFloat(item.valor_compra || 0) 
            : parseFloat(item.valor_venda || 0);

        const precoFinalMantido = parseFloat(item.preco_final || 0);

        let novoDesconto = 0;
        if (novoPrecoOriginal > 0) {
            novoDesconto = ((novoPrecoOriginal - precoFinalMantido) / novoPrecoOriginal) * 100;
        }

        return {
            ...item,
            preco_original: novoPrecoOriginal,
            desconto_percentual: novoDesconto.toFixed(2),
            preco_final: precoFinalMantido.toFixed(2)
        };
    }));
  }, [formData.tipo_precificacao]);

  const handleSelectProduct = (prod) => {
    setSelectedProduct(prod);
    setSearchTerm(prod.nome + " - " + prod.codigo_global);
    setFilteredProducts([]);
    setDescontoInput("");
    setPrecoFinalInput(""); 
  };

  const handleAddNewItem = () => {
    if (!selectedProduct) return;
    
    const jaExiste = existingItems.some(i => i.produto_id === selectedProduct.produto_id) || 
                     newManualItems.some(i => i.produto_id === selectedProduct.produto_id);

    if (jaExiste) {
        toast({ title: "Produto já está na lista.", status: "warning" });
        return;
    }

    const precoOriginal = formData.tipo_precificacao === 'automatica'
        ? parseFloat(selectedProduct.valor_compra)
        : parseFloat(selectedProduct.valor_venda);

    let desconto = parseFloat(descontoInput) || 0;
    let final = parseFloat(precoFinalInput) || 0;

    if(final > 0) {
        desconto = ((precoOriginal - final) / precoOriginal) * 100;
    } else {
        final = precoOriginal - (precoOriginal * (desconto / 100));
    }

    const novoItem = {
        ...selectedProduct,
        produto_id: selectedProduct.produto_id,
        nome: selectedProduct.nome,
        codigo_global: selectedProduct.codigo_global,
        codigo_fornecedor: selectedProduct.codigo_fornecedor,
        fabricante_nome: selectedProduct.fabricante_nome,
        preco_original: precoOriginal,
        desconto_percentual: desconto.toFixed(2),
        preco_final: final.toFixed(2)
    };

    setNewManualItems([...newManualItems, novoItem]);
    setSelectedProduct(null);
    setSearchTerm("");
    setDescontoInput("");
    setPrecoFinalInput("");
  };

  const handleRemoveNewItem = (index) => {
      const items = [...newManualItems];
      items.splice(index, 1);
      setNewManualItems(items);
  };

  const confirmDeleteExisting = async () => {
      if (!itemToDelete) return;
      try {
          await api.delete(`/lista-descontos/item/${itemToDelete}`);
          toast({ title: "Item removido.", status: "success" });
          fetchExistingItems(existingPagination.current_page);
      } catch (error) {
          toast({ title: "Erro ao remover item.", status: "error" });
      } finally {
          setItemToDelete(null);
      }
  };

  const handleUpdate = async () => {
    setSaving(true);
    const data = new FormData();

    data.append("titulo", formData.titulo);
    data.append("descricao", formData.descricao);
    data.append("origem", formData.origem);
    data.append("status", formData.status);
    data.append("data_inicio", formData.data_inicio);
    data.append("tipo_precificacao", formData.tipo_precificacao);
    if(formData.data_fim) data.append("data_fim", formData.data_fim);

    if (arquivo) {
        data.append("arquivo", arquivo);
    } 
    
    if (newManualItems.length > 0) {
        newManualItems.forEach((item, index) => {
            data.append(`itens_manual[${index}][produto_id]`, item.produto_id);
            data.append(`itens_manual[${index}][desconto_percentual]`, item.desconto_percentual);
            data.append(`itens_manual[${index}][preco_final]`, item.preco_final);
        });
    }

    try {
        await api.post(`/lista-descontos/${id}`, data, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        toast({ title: "Lista atualizada com sucesso!", status: "success" });

        setNewManualItems([]);
        setArquivo(null);
        fetchLista();
        fetchExistingItems(1);
    } catch (error) {
        console.error(error);
        toast({ title: "Erro ao atualizar.", description: error.response?.data?.msg, status: "error" });
    } finally {
        setSaving(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => { setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setArquivo(e.dataTransfer.files[0]);
    }
  };

  const handleDownloadModel = () => {
    const header = "SKU_TAGA;CODIGO_FORNECEDOR;PRECO_COMPRA;PORCENTAGEM_DESCONTO";
    const row1 = "12345;;95.50;"; 
    const row2 = ";COD-ABC-99;;10.00";
    const csvContent = `${header}\n${row1}\n${row2}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "modelo_promocao_taga.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loadingData) return <Flex justify="center" h="100vh" align="center"><Preloader /></Flex>;

  return (
    <Box bg="gray.50" minH="100vh" py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl">
        <Flex mb={6} justify="space-between" align="center">
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate("/promocoes")}>
                Voltar
            </Button>
            <Heading size="lg" color="orange.500">Editar Lista #{id}</Heading>
            <Box w="100px"/>
        </Flex>
        
        <Flex gap={6} direction={{ base: "column", lg: "row" }}>
            
            <Box flex="1" bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="sm" h="fit-content">
                <VStack spacing={4}>
                    <FormControl isRequired>
                        <FormLabel>Título</FormLabel>
                        <Input value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Descrição</FormLabel>
                        <Textarea value={formData.descricao} onChange={(e) => setFormData({...formData, descricao: e.target.value})} />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel>Status</FormLabel>
                        <Select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                            <option value="ativa">Ativa</option>
                            <option value="pausada">Pausada</option>
                            <option value="concluida">Concluída</option>
                        </Select>
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel>Origem</FormLabel>
                        <Select value={formData.origem} onChange={(e) => setFormData({...formData, origem: e.target.value})}>
                            <option value="0">TAGA</option>
                            <option value="1">MORELATE</option>
                            <option value="2">VANNUCCI</option>
                            <option value="3">ISAPA</option>
                            <option value="4">BRCOMPANY</option>
                            <option value="5">AUTOLATINA</option>
                            <option value="6">LNG</option>
                            <option value="7">CBA</option>
                            <option value="8">MSAM</option>
                            <option value="9">BLPARTS</option>
                            <option value="100">OUTROS</option>
                        </Select>
                    </FormControl>

                    <FormControl as="fieldset" borderWidth="1px" p={3} borderRadius="md" borderColor="orange.200" bg="orange.50">
                        <FormLabel as="legend" fontWeight="bold" fontSize="sm" color="orange.600" px={1}>Estratégia de Precificação</FormLabel>
                        <RadioGroup value={formData.tipo_precificacao} onChange={(val) => setFormData({...formData, tipo_precificacao: val})}>
                            <Stack>
                                <Radio value="automatica" colorScheme="orange">
                                    <Text fontSize="sm"><b>Automática:</b> Valor informado é o novo <b>Custo de Compra</b>.</Text>
                                </Radio>
                                <Radio value="manual" colorScheme="orange">
                                    <Text fontSize="sm"><b>Direta:</b> Valor informado é o novo <b>Preço de Venda</b>.</Text>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                    </FormControl>

                    <Flex gap={4} w="100%" direction={{ base: "column", md: "row" }}>
                        <FormControl isRequired>
                            <FormLabel>Início</FormLabel>
                            <Input type="date" value={formData.data_inicio} onChange={(e) => setFormData({...formData, data_inicio: e.target.value})} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Fim</FormLabel>
                            <Input type="date" value={formData.data_fim} onChange={(e) => setFormData({...formData, data_fim: e.target.value})} />
                        </FormControl>
                    </Flex>
                    {saving ? <Preloader /> : (
                        <Button w="100%" colorScheme="green" onClick={handleUpdate} mt={4}>
                            Salvar Alterações
                        </Button>
                    )}
                </VStack>
            </Box>

            <Box flex="2" bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="sm">
                <Tabs variant="enclosed" colorScheme="orange" isFitted>
                    <TabList mb={4} flexDirection={{ base: "column", md: "row" }}>
                        <Tab fontWeight="bold" mb={{ base: 2, md: 0 }}>Itens Atuais ({existingPagination.total})</Tab>
                        <Tab fontWeight="bold">Adicionar Novos</Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel px={0}>
                            <Flex mb={3} gap={2} align="center">
                                <InputGroup maxW="280px" size="sm">
                                    <InputLeftElement pointerEvents="none">
                                        <SearchIcon color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Buscar por nome, SKU, código..."
                                        value={existingBusca}
                                        onChange={(e) => setExistingBusca(e.target.value)}
                                        bg="gray.50"
                                    />
                                </InputGroup>
                            </Flex>

                            {existingLoading ? (
                                <Flex justify="center" py={8}><Spinner size="lg" color="orange.400" /></Flex>
                            ) : (
                            <>
                            <Box maxH="500px" overflowY="auto">
                                <Table size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Produto</Th>
                                            <Th display={{ base: "none", md: "table-cell" }}>SKU</Th>
                                            <Th display={{ base: "none", md: "table-cell" }}>Cód. Forn.</Th>
                                            <Th isNumeric>Original</Th>
                                            <Th isNumeric>Desc.</Th>
                                            <Th isNumeric>Final</Th>
                                            <Th></Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {existingItems.map((item) => {
                                            const produtoParaImagem = {
                                                ...item.produto,
                                                codigo_fornecedor: item.codigo_fornecedor,
                                                fornecedor_id: item.fornecedor_id ? parseInt(item.fornecedor_id, 10) : null
                                            };

                                            return (
                                                <Tr key={item.id}>
                                                    <Td>
                                                        <Flex align="center" gap={2}>
                                                            <Box boxSize="40px" minW="40px" borderRadius="md" overflow="hidden" border="1px solid #eee">
                                                                <ProdutoImagem produto={produtoParaImagem} width="100%" height="100%" />
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" fontWeight="bold" noOfLines={2} title={item.produto?.nome}>{item.produto?.nome}</Text>
                                                                <Text fontSize="2xs" color="gray.500">{item.produto?.codigo_global}</Text>
                                                            </Box>
                                                        </Flex>
                                                    </Td>
                                                    <Td display={{ base: "none", md: "table-cell" }}>{item.produto_id}</Td>
                                                    <Td display={{ base: "none", md: "table-cell" }}>{item.codigo_fornecedor || "-"}</Td>
                                                    <Td isNumeric>R$ {item.preco_venda_original}</Td>
                                                    <Td isNumeric color="red.500">{item.porcentagem_desconto_preco_venda}%</Td>
                                                    <Td isNumeric fontWeight="bold" color="green.600">R$ {item.preco_venda_promocional}</Td>
                                                    <Td>
                                                        <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => setItemToDelete(item.id)} />
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                        {existingItems.length === 0 && <Tr><Td colSpan={7} textAlign="center" py={4} color="gray.500">Lista vazia.</Td></Tr>}
                                    </Tbody>
                                </Table>
                            </Box>

                            {existingPagination.last_page > 1 && (
                                <Flex justify="space-between" align="center" mt={3} flexWrap="wrap" gap={2}>
                                    <Flex align="center" gap={2}>
                                        <Text fontSize="xs" color="gray.600">
                                            {existingPagination.total} itens | Pág. {existingPagination.current_page}/{existingPagination.last_page}
                                        </Text>
                                        <Select size="xs" w="65px" value={existingPerPage} onChange={(e) => setExistingPerPage(Number(e.target.value))}>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </Select>
                                    </Flex>
                                    <Flex gap={1}>
                                        <Button size="xs" leftIcon={<ChevronLeftIcon />}
                                            onClick={() => fetchExistingItems(existingPagination.current_page - 1)}
                                            isDisabled={existingPagination.current_page <= 1}>
                                            Anterior
                                        </Button>
                                        <Button size="xs" rightIcon={<ChevronRightIcon />}
                                            onClick={() => fetchExistingItems(existingPagination.current_page + 1)}
                                            isDisabled={existingPagination.current_page >= existingPagination.last_page}>
                                            Próxima
                                        </Button>
                                    </Flex>
                                </Flex>
                            )}
                            </>
                            )}
                        </TabPanel>

                        <TabPanel px={0}>
                            <Box mb={4} position="relative" mt={2}>
                                <FormLabel>Buscar Produto</FormLabel>
                                <InputGroup>
                                    <Input placeholder="Digite para buscar..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); if(selectedProduct) setSelectedProduct(null); }} bg="gray.50" />
                                    <InputRightElement>{isSearching ? <Spinner size="sm" /> : <SearchIcon color="gray.400"/>}</InputRightElement>
                                </InputGroup>

                                {(isSearching || (filteredProducts.length > 0 && !selectedProduct)) && (
                                    <Box position="absolute" zIndex="999" bg="white" border="1px solid #e2e8f0" w="100%" maxHeight="400px" overflowY="auto" borderRadius="md" shadow="xl" mt={1}>
                                            {isSearching ? (
                                                <VStack p={4} spacing={4} align="stretch">
                                                    {[1,2].map(i => <Skeleton key={i} height="50px" borderRadius="md"/>)}
                                                </VStack>
                                            ) : (
                                                filteredProducts.map(prod => (
                                                    <Flex key={prod.produto_id} p={3} borderBottom="1px solid #f0f0f0" _hover={{ bg: "orange.50", cursor: "pointer" }} onClick={() => handleSelectProduct(prod)} align="start" gap={4}>
                                                        <Box boxSize="60px" flexShrink={0} borderRadius="md" overflow="hidden" border="1px solid #eee">
                                                            <ProdutoImagem produto={prod} width="100%" height="100%" />
                                                        </Box>
                                                        <Box flex={1}>
                                                            <Text fontWeight="bold" fontSize="sm" mb={1} color="gray.700">{prod.nome}</Text>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={1} fontSize="xs" color="gray.500">
                                                                <GridItem><b>SKU:</b> {prod.produto_id}</GridItem>
                                                                <GridItem><b>Global:</b> {prod.codigo_global}</GridItem>
                                                                <GridItem><b>Forn:</b> {prod.codigo_fornecedor || "-"}</GridItem>
                                                                <GridItem><b>Fab:</b> {prod.fabricante_nome}</GridItem>
                                                            </Grid>
                                                            <Flex mt={2} gap={2} fontSize="xs" wrap="wrap">
                                                                <Badge colorScheme="blue">Venda: R$ {prod.valor_venda}</Badge>
                                                                <Badge colorScheme="purple">Compra: R$ {prod.valor_compra}</Badge>
                                                            </Flex>
                                                        </Box>
                                                    </Flex>
                                                ))
                                            )}
                                    </Box>
                                )}
                            </Box>

                            <Alert status="info" mb={4} fontSize="sm" borderRadius="md">
                                <AlertIcon />
                                {formData.tipo_precificacao === 'automatica' 
                                    ? "Modo Automático: O valor inserido é o novo Custo."
                                    : "Modo Direto: O valor inserido é o Preço Final de Venda."}
                            </Alert>

                            <Flex 
                                gap={3} 
                                align={{ base: "stretch", md: "flex-end" }} 
                                mb={6} 
                                bg="gray.50" 
                                p={4} 
                                borderRadius="md" 
                                direction={{ base: "column", sm: "row" }}
                            >
                                <FormControl flex={1}>
                                    <FormLabel fontSize="sm">Desconto (%)</FormLabel>
                                    <NumberInput min={0} max={100} value={descontoInput} onChange={(val) => { setDescontoInput(val); setPrecoFinalInput(""); }}>
                                        <NumberInputField bg="white" />
                                    </NumberInput>
                                </FormControl>
                                <FormControl flex={1}>
                                    <FormLabel fontSize="sm">Valor (R$)</FormLabel>
                                    <NumberInput min={0} value={precoFinalInput} onChange={(val) => { setPrecoFinalInput(val); setDescontoInput(""); }}>
                                        <NumberInputField bg="white" />
                                    </NumberInput>
                                </FormControl>
                                <Button 
                                    leftIcon={<CheckIcon />} 
                                    colorScheme="blue" 
                                    onClick={handleAddNewItem} 
                                    isDisabled={!selectedProduct}
                                    flexShrink={0}
                                    minW="140px"
                                >
                                    Adicionar
                                </Button>
                            </Flex>

                            <Divider my={4} />

                            <Box mb={4}>
                                <Text fontWeight="bold" fontSize="sm" mb={1} color="blue.600">Novos Itens a Salvar ({newManualItems.length})</Text>
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Produto</Th>
                                            <Th display={{ base: "none", md: "table-cell" }}>SKU</Th>
                                            <Th display={{ base: "none", md: "table-cell" }}>Cód. Forn.</Th>
                                            <Th isNumeric>Original</Th>
                                            <Th isNumeric>Desc.</Th>
                                            <Th isNumeric>Final</Th>
                                            <Th></Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {newManualItems.map((item, idx) => (
                                            <Tr key={idx} bg="blue.50">
                                                <Td>
                                                    <Flex align="center" gap={2}>
                                                        <Box boxSize="40px" minW="40px" borderRadius="md" overflow="hidden" border="1px solid #eee">
                                                            <ProdutoImagem produto={item} width="100%" height="100%" />
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="xs" fontWeight="bold" noOfLines={2}>{item.nome}</Text>
                                                            <Text fontSize="2xs" color="gray.500">{item.codigo_global}</Text>
                                                        </Box>
                                                    </Flex>
                                                </Td>
                                                <Td display={{ base: "none", md: "table-cell" }}>{item.produto_id}</Td>
                                                <Td display={{ base: "none", md: "table-cell" }}>{item.codigo_fornecedor}</Td>
                                                <Td isNumeric>R$ {item.preco_original}</Td>
                                                <Td isNumeric color="blue.500">{item.desconto_percentual}%</Td>
                                                <Td isNumeric fontWeight="bold">R$ {item.preco_final}</Td>
                                                <Td><IconButton icon={<DeleteIcon/>} size="xs" onClick={() => handleRemoveNewItem(idx)}/></Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>

                            <Divider my={4} />

                            <VStack spacing={4} align="start">
                                <Text fontWeight="bold">Ou Importar Arquivo</Text>
                                <Alert status="warning" fontSize="sm" borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        A coluna <b>&nbsp;VALOR&nbsp;</b> será interpretada como 
                                        <b>&nbsp;{formData.tipo_precificacao === 'automatica' ? 'NOVO CUSTO DE COMPRA' : 'NOVO PREÇO DE VENDA'}</b>.
                                    </Box>
                                </Alert>
                                <Button leftIcon={<DownloadIcon />} variant="outline" size="sm" onClick={handleDownloadModel}>Baixar Modelo.csv</Button>
                                
                                <Box 
                                    w="100%" 
                                    h="120px" 
                                    border="2px dashed" 
                                    borderColor={isDragOver ? "orange.400" : "gray.300"} 
                                    borderRadius="md" 
                                    bg={isDragOver ? "orange.50" : "gray.50"}
                                    display="flex" 
                                    flexDirection="column" 
                                    alignItems="center" 
                                    justifyContent="center"
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    cursor="pointer"
                                    onClick={() => fileInputRef.current.click()}
                                    transition="all 0.2s"
                                >
                                    <AttachmentIcon boxSize={6} color="gray.400" mb={2} />
                                    <Text fontSize="sm" color="gray.600" fontWeight="bold">
                                        {arquivo ? arquivo.name : "Clique ou arraste o arquivo aqui"}
                                    </Text>
                                    <Input type="file" accept=".csv,.txt" display="none" ref={fileInputRef} onChange={(e) => setArquivo(e.target.files[0])} />
                                </Box>
                            </VStack>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Flex>

        <AlertDialog isOpen={!!itemToDelete} leastDestructiveRef={cancelRef} onClose={() => setItemToDelete(null)}>
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader>Remover Item</AlertDialogHeader>
                    <AlertDialogBody>
                        Tem certeza que deseja remover este item da lista de promoção? Essa ação é imediata.
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={() => setItemToDelete(null)}>Cancelar</Button>
                        <Button colorScheme="red" onClick={confirmDeleteExisting} ml={3}>Remover</Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>

      </Container>
    </Box>
  );
};

export default EditarListaPromocao;